import React, { useCallback, useEffect, useMemo } from 'react';
import IconMap from '../icons/IconMap';
import IconReport from '../icons/IconReport';
import IconList from '../icons/IconList';
import IconHeart from '../icons/IconHeart';
import IconSearch from '../icons/IconSearch';
import IconPrint from '../icons/IconPrint';
import IconArrowDown from '../icons/IconArrowDown';
import IconMarker from '../icons/IconMarker';
import IconEndNav from '../icons/IconEndNav';
import SlideToggle from 'react-slide-toggle';
import useUI from '../hooks/useUI';
import useApp from '../hooks/useApp';
import useData from '../hooks/useData';
import { uniq } from 'underscore';

const LeftPanel = () => {
	const { closeAllUI, toggleUI, ExhibitorList, MobileMenu } = useUI();
	const { isCHT, activeFloor, setExhibitorSection, exhibitorSection, setCHT, setENG, setAppState, currentNavStart, setCurrentNavStart } = useApp();
	const { basicInfo, exhibitorInfo, navigationInfo, floorInfo } = useData();

	const showMap = useCallback(() => {
		closeAllUI();
	}, [closeAllUI]);

	const showExhibitionInfo = useCallback(() => {
		toggleUI('ExhibitionInfo');
	}, [toggleUI]);

	const showExhibitorList = useCallback((section) => {
		if (!ExhibitorList) toggleUI('ExhibitorList');
		if (section) setExhibitorSection(section);
	}, [ExhibitorList, setExhibitorSection, toggleUI]);

	const showMyFavourites = useCallback(() => {
		toggleUI('MyFavourites');
	}, [toggleUI]);

	const showSearch = useCallback(() => {
		toggleUI('SearchPanel');
	}, [toggleUI]);

	const currentUrl = window.location.href ? window.location.href.split('?')[0] : '';

	const exhibitorSections = useMemo(() => {
		const key = isCHT ? '區域-中文' : '區域-英文';
		const uniqueSections = uniq(exhibitorInfo.map(exhibitor => exhibitor[key]));
		return uniqueSections;
	}, [exhibitorInfo, isCHT]);

	const toggleMenu = useCallback(() => {
		toggleUI('MobileMenu');
	}, [toggleUI]);

	const toggleLanguage = useCallback(() => {
		if (isCHT) setENG();
		else setCHT();
	}, [isCHT, setCHT, setENG]);

	const stopNavigation = useCallback(() => {
		setAppState((appState) => {
			return {
				...appState,
				navigation: false,
			}
		});
	}, [setAppState]);

	const currentFloor = useMemo(() => {
		return floorInfo[activeFloor];
	}, [activeFloor, floorInfo]);

	const currentFloorNavigationInfo = useMemo(() => {
		return navigationInfo.filter(nav => nav['樓層數'] === currentFloor.floor.toString());
	}, [currentFloor, navigationInfo]);

	useEffect(() => {
		const defaultNavStart = currentFloorNavigationInfo.filter(nav => nav['預設'] === '1')[0] || currentFloorNavigationInfo[0];
		setCurrentNavStart(defaultNavStart);
	}, [activeFloor, currentFloorNavigationInfo, setCurrentNavStart]);
	return (
		<div id='LeftPanel'>
			<div className='mobile-menu'>
				<div className='exhibition-name'>
					{isCHT ? basicInfo.nameCHT : basicInfo.nameENG}
				</div>

				<div className='controls'>
					<div className='language-chooser' onClick={toggleLanguage}>{isCHT ? 'En' : '中'}</div>
					<div className='menu-toggler' onClick={toggleMenu}></div>
				</div>
			</div>

			<div className={`mobile-drawer ${MobileMenu ? 'active' : ''}`}>
				<div className='control-item' onClick={showMap}>
					<IconMap />
					<div className='text'>{isCHT ? '展會地圖' : 'Exhibition Map'}</div>
				</div>

				<div className='control-item' onClick={showExhibitionInfo}>
					<IconReport />
					<div className='text'>{isCHT ? '展會簡介' : 'Exhibition Info'}</div>
				</div>

				<SlideToggle collapsed>
					{({ toggle, setCollapsibleElement }) => (
						<div>
							<div className='control-item' onClick={toggle}>
								<IconList />
								<div className='text'>{isCHT ? '廠商名單' : 'Exhibitor List'}</div>
								<IconArrowDown />
							</div>

							<div className='collapsible' ref={setCollapsibleElement}>
								<div className={`control-item sub ${exhibitorSection === 'all' ? 'active' : ''}`} onClick={() => { showExhibitorList('all') }}>
									<div className='text'>{isCHT ? '全部' : 'All'}</div>
								</div>
								{exhibitorSections.map((section, index) => {
									return (
										<div className={`control-item sub ${exhibitorSection === section ? 'active' : ''}`} key={section + index} onClick={() => { showExhibitorList(section) }}>
											<div className='text'>{section}</div>
										</div>
									)
								})}
							</div>
						</div>
					)}
				</SlideToggle>

				<div className='control-item' onClick={showMyFavourites}>
					<IconHeart />
					<div className='text'>{isCHT ? '我的最愛' : 'Saved Exhibitors'}</div>
				</div>

				<SlideToggle collapsed>
					{({ toggle, setCollapsibleElement }) => (
						<div>
							<div className='control-item' onClick={toggle}>
								<IconMarker />
								<div className='text'>{isCHT ? '導航起點' : 'Navigation Starting Points'}</div>
								<IconArrowDown />
							</div>

							<div className='collapsible' ref={setCollapsibleElement}>
								{currentFloorNavigationInfo.map((nav, index) => {
									return (
										<div className={`control-item sub ${currentNavStart?.['序號'] === nav['序號'] ? 'active' : ''}`} key={nav['序號'] + index} onClick={() => { setCurrentNavStart(nav) }}>
											<div className='text'>{isCHT ? nav['中文名稱'] : nav['英文名稱']}</div>
										</div>
									)
								})}
							</div>
						</div>
					)}
				</SlideToggle>

				<div className='control-item' onClick={stopNavigation}>
					<IconEndNav />
					<div className='text'>{isCHT ? '結束導航' : 'Stop Navigation'}</div>
				</div>

				<div className='control-item' onClick={showSearch}>
					<IconSearch />
					<div className='text'>{isCHT ? '進階搜尋' : 'Advanced Search'}</div>
				</div>

				<SlideToggle collapsed>
					{({ toggle, setCollapsibleElement }) => (
						<div>
							<div className='control-item' onClick={toggle}>
								<IconPrint />
								<div className='text'>{isCHT ? '下載/列印' : 'Download/Print'}</div>
								<IconArrowDown />
							</div>

							<div className='collapsible' ref={setCollapsibleElement}>
								<a className='control-item sub' href={`${currentUrl}?page=single-floor&floor=${activeFloor}&lang=${isCHT ? 'CHT' : 'ENG'}`} target='_blank' rel='noreferrer noopener'>
									<div className='text'>{isCHT ? '單樓層' : 'Single Floor'}</div>
								</a>

								<a className='control-item sub' href={`${currentUrl}?page=multi-floor&lang=${isCHT ? 'CHT' : 'ENG'}`} target='_blank' rel='noreferrer noopener'>
									<div className='text'>{isCHT ? '全部地圖' : 'All Maps'}</div>
								</a>

								<a className='control-item sub' href={`${currentUrl}?page=exhibitor-list&lang=${isCHT ? 'CHT' : 'ENG'}`} target='_blank' rel='noreferrer noopener'>
									<div className='text'>{isCHT ? '展商名單' : 'Exhibitor List'}</div>
								</a>
							</div>
						</div>
					)}
				</SlideToggle>
			</div>
		</div>
	)
}

export default LeftPanel;